import { useCallback, useRef } from "react";
import { usePaneStore } from "../state/store-context";
import { usePreferences } from "@/hooks/usePreferences";

/**
 * R-149 — Persist the operator's current viewer zoom (viewport.scale) to
 * OperatorPreference.viewerZoom on demand, debounced so each
 * mouse-wheel tick doesn't slam the API.
 *
 * The hook returns a `persistZoom(scale)` callback. Call it from explicit
 * user-initiated zoom actions (mouse wheel, fit-to-width button,
 * double-click). It is intentionally NOT a passive `useEffect` watcher on
 * viewport.scale — that would also capture the bitmap-load auto-fit and
 * overwrite the user's saved zoom on every doc transition.
 *
 * Together with the bitmap-load effect in DocumentCanvas (which seeds
 * viewport.scale from `savedViewerZoom` instead of fit-to-width when
 * present), this makes the operator's zoom carry across document
 * transitions.
 */
const DEBOUNCE_MS = 500;

export function useViewerZoomPersist(): (scale: number) => void {
  const setSavedViewerZoom = usePaneStore((s) => s.setSavedViewerZoom);
  const { updatePreference } = usePreferences();

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPersistedRef = useRef<number | null>(null);

  return useCallback(
    (scale: number) => {
      // Skip if scale hasn't meaningfully changed from what's already persisted.
      // (Sub-1% drift = noise from container-resize fit recalcs.)
      if (lastPersistedRef.current !== null) {
        const ratio = scale / lastPersistedRef.current;
        if (ratio > 0.99 && ratio < 1.01) return;
      }

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        lastPersistedRef.current = scale;
        setSavedViewerZoom(scale);
        updatePreference("viewerZoom", scale);
      }, DEBOUNCE_MS);
    },
    [setSavedViewerZoom, updatePreference],
  );
}
